import { useEffect, useState, useCallback, useMemo } from "react";
import {
 HiOutlineTrash,
 HiOutlineSearch,
 HiOutlineX,
 HiOutlineRefresh,
 HiOutlineExclamationCircle,
 HiOutlineLockClosed,
} from "react-icons/hi";
import {
 fetchMasterData,
 fetchSchools,
 fetchStudents,
 fetchCompetencies,
 fetchAllSets,
 restoreSchool,
 restoreStudent,
 restoreState,
 restoreDistrict,
 restoreBlock,
 restoreCompetency,
 restoreSet,
} from "../services/masterDataApi";
import { fetchUsers, restoreUser, resolveUserNames } from "../services/userManagementApi";
import Toast from "../components/Toast";
import Pagination from "../components/Pagination";
import FilterDropdown from "../components/FilterDropdown";
import { useDebounce } from "../hooks/useDebounce";
import { useUserRole } from "../hooks/useUserRole";

const PAGE_SIZE = 20; // single entity-type table listing
const ALL_PAGE_SIZE = 50; // per-type cap when "All" is selected (occasional admin cleanup, not bulk browsing)

/**
 * Per-entity-type configuration: how to fetch deleted rows, how to extract
 * display fields from a raw row, and how to restore a row.
 *
 * `supportsSearch`  — whether the underlying endpoint accepts a `search` param.
 * `supportsState`   — "direct" (endpoint accepts state_id), "client" (row has
 *                      state_id but the endpoint doesn't filter by it, so we
 *                      filter client-side), or false (not applicable / not
 *                      available at all).
 */
const ENTITY_CONFIG = {
 school: {
  label: "School",
  entityName: "schools",
  badge: "bg-blue-50 text-blue-700",
  supportsSearch: true,
  supportsState: "direct",
  fetch: (params) => fetchSchools({ ...params, deleted_only: true }),
  rowKey: (r) => `school-${r.id}`,
  getName: (r) => r.name || `School #${r.id}`,
  getContext: (r) => [r.block_name, r.district_name, r.state_name].filter(Boolean).join(" / ") || "—",
  restore: (r) => restoreSchool(r.id),
 },
 student: {
  label: "Student",
  entityName: "students",
  badge: "bg-teal-50 text-teal-700",
  // The /school/students/ endpoint only accepts school_id/grade — no search
  // or state_id filter (see services/school/app/api/students.py). Both
  // filters are simply not applied for this entity type.
  supportsSearch: false,
  supportsState: false,
  fetch: (params) => fetchStudents({ ...params, deleted_only: true }),
  rowKey: (r) => `student-${r.id}`,
  getName: (r) => r.name || r.srn || `Student #${r.id}`,
  getContext: (r) => (r.school_id != null ? `School ID: ${r.school_id}` : "—"),
  restore: (r) => restoreStudent(r.id),
 },
 user: {
  label: "User",
  entityName: "users",
  badge: "bg-purple-50 text-purple-700",
  supportsSearch: true,
  // The users endpoint doesn't accept a state_id query param, but each row
  // already carries the user's state_id — filter client-side instead.
  supportsState: "client",
  fetch: (params) => fetchUsers({ ...params, deleted_only: true }),
  rowKey: (r) => `user-${r.id}`,
  getName: (r) => r.username || r.email || r.id,
  getContext: (r) => r.role || "—",
  restore: (r) => restoreUser(r.id),
 },
 state: {
  label: "State",
  entityName: "states",
  badge: "bg-amber-50 text-amber-700",
  supportsSearch: true,
  supportsState: false, // a "State" row IS the state — no state_id to filter by
  fetch: (params) => fetchMasterData("states", { ...params, deleted_only: true }),
  rowKey: (r) => `state-${r.id}`,
  getName: (r) => r.name || `State #${r.id}`,
  getContext: () => "—",
  restore: (r) => restoreState(r.id),
 },
 district: {
  label: "District",
  entityName: "districts",
  badge: "bg-indigo-50 text-indigo-700",
  supportsSearch: true,
  supportsState: "direct",
  fetch: (params) => fetchMasterData("districts", { ...params, deleted_only: true }),
  rowKey: (r) => `district-${r.id}`,
  getName: (r) => r.name || `District #${r.id}`,
  getContext: (r) => r.state_name || "—",
  restore: (r) => restoreDistrict(r.id),
 },
 block: {
  label: "Block",
  entityName: "blocks",
  badge: "bg-pink-50 text-pink-700",
  supportsSearch: true,
  supportsState: "direct",
  fetch: (params) => fetchMasterData("blocks", { ...params, deleted_only: true }),
  rowKey: (r) => `block-${r.id}`,
  getName: (r) => r.name || `Block #${r.id}`,
  getContext: (r) => [r.district_name, r.state_name].filter(Boolean).join(" / ") || "—",
  restore: (r) => restoreBlock(r.id),
 },
 competency: {
  label: "Competency",
  entityName: "competencies",
  badge: "bg-cyan-50 text-cyan-700",
  // The competencies list endpoint accepts state_id but not search.
  supportsSearch: false,
  supportsState: "direct",
  fetch: (params) => fetchCompetencies({ ...params, deleted_only: true }),
  rowKey: (r) => `competency-${r.code}-${r.state_id ?? "global"}`,
  getName: (r) => r.name || r.code,
  getContext: (r) => (r.state_id != null ? `State ID: ${r.state_id}` : "Global"),
  restore: (r) => restoreCompetency(r.code, r.state_id),
 },
 set: {
  label: "Competency Set",
  entityName: "competency sets",
  badge: "bg-lime-50 text-lime-700",
  supportsSearch: true,
  // Set rows carry a competency_code but not a state_id — no state filtering possible.
  supportsState: false,
  fetch: (params) => fetchAllSets({ ...params, deleted_only: true }),
  rowKey: (r) => `set-${r.id}`,
  getName: (r) => r.title || `Set #${r.id}`,
  getContext: (r) => r.competency_code || "—",
  restore: (r) => restoreSet(r.id),
 },
};

const ENTITY_TYPES = Object.keys(ENTITY_CONFIG);

const ENTITY_OPTIONS = [
 { value: "", label: "All" },
 ...ENTITY_TYPES.map((t) => ({ value: t, label: ENTITY_CONFIG[t].label })),
];

function tagRow(type, raw) {
 const config = ENTITY_CONFIG[type];
 return {
  key: config.rowKey(raw),
  type,
  name: config.getName(raw),
  context: config.getContext(raw),
  deletedAt: raw.deleted_at || null,
  deletedBy: raw.deleted_by || null,
  raw,
 };
}

function formatDate(value) {
 if (!value) return "—";
 const d = new Date(value);
 if (Number.isNaN(d.getTime())) return "—";
 return d.toLocaleString();
}

function RestoreCenter() {
 const { canManageCompetencies, loading: roleLoading } = useUserRole();

 const [entityType, setEntityType] = useState(""); // "" == All
 const [search, setSearch] = useState("");
 const debouncedSearch = useDebounce(search, 400);
 const [filters, setFilters] = useState({ state_id: "" });
 const [states, setStates] = useState([]);

 const [rows, setRows] = useState([]);
 const [loading, setLoading] = useState(true);
 const [toast, setToast] = useState(null);
 const [restoringKey, setRestoringKey] = useState(null);
 const [notices, setNotices] = useState([]); // info/warning strings shown above the table (overflow, failures)
 const [userNames, setUserNames] = useState({}); // { [keycloakUserId]: displayName } for the "Deleted By" column

 // Resolve "Deleted By" Keycloak IDs to display names whenever new rows
 // bring in IDs we haven't looked up yet — the raw sub is meaningless to
 // an admin reading this page.
 useEffect(() => {
  const unresolved = [...new Set(rows.map((r) => r.deletedBy).filter(Boolean))]
   .filter((id) => !(id in userNames));
  if (unresolved.length === 0) return;
  resolveUserNames(unresolved)
   .then((resolved) => {
    // Mark ids the lookup didn't find (e.g. a deleted/service account) as
    // null rather than leaving them missing, so we don't keep re-querying
    // the same unresolvable id on every subsequent row reload.
    const withMisses = { ...resolved };
    unresolved.forEach((id) => { if (!(id in withMisses)) withMisses[id] = null; });
    setUserNames((prev) => ({ ...prev, ...withMisses }));
   })
   .catch(() => {}); // non-critical — falls back to showing the raw ID
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [rows]);

 // Pagination — only meaningful when a specific entity type is selected.
 const [currentPage, setCurrentPage] = useState(1);
 const [totalCount, setTotalCount] = useState(0);
 const [totalPages, setTotalPages] = useState(1);

 // Nothing on this page is shown until the role check passes — don't fire
 // any request for a user who will only see "Access Restricted".
 const canLoad = !roleLoading && canManageCompetencies;

 // Load states once for the State filter dropdown.
 useEffect(() => {
  if (!canLoad) return;
  fetchMasterData("states", { page: 1, page_size: 100, search: "" })
   .then((res) => { if (res.success) setStates(res.data || []); })
   .catch(() => setStates([]));
 }, [canLoad]);

 const loadSingle = useCallback(async (type, page) => {
  const config = ENTITY_CONFIG[type];
  const params = { page, page_size: PAGE_SIZE };
  if (config.supportsSearch) params.search = debouncedSearch;
  if (config.supportsState === "direct" && filters.state_id) params.state_id = Number(filters.state_id);

  const res = await config.fetch(params);
  let tagged = (res.data || []).map((r) => tagRow(type, r));

  if (config.supportsState === "client" && filters.state_id) {
   tagged = tagged.filter((row) => String(row.raw.state_id) === String(filters.state_id));
  }

  const total = res.total_count ?? tagged.length;
  const pages = res.total_pages ?? Math.max(1, Math.ceil(total / PAGE_SIZE));

  setRows(tagged);
  setTotalCount(total);
  setTotalPages(pages);
  setCurrentPage(res.current_page || page);
  setNotices([]);
 }, [debouncedSearch, filters.state_id]);

 const loadAll = useCallback(async () => {
  const settled = await Promise.allSettled(
   ENTITY_TYPES.map((type) => {
    const config = ENTITY_CONFIG[type];
    const params = { page: 1, page_size: ALL_PAGE_SIZE };
    if (config.supportsSearch) params.search = debouncedSearch;
    if (config.supportsState === "direct" && filters.state_id) params.state_id = Number(filters.state_id);
    return config.fetch(params);
   })
  );

  let merged = [];
  const newNotices = [];

  settled.forEach((result, idx) => {
   const type = ENTITY_TYPES[idx];
   const config = ENTITY_CONFIG[type];
   if (result.status !== "fulfilled") {
    newNotices.push({ kind: "error", text: `Failed to load deleted ${config.entityName}.` });
    return;
   }
   const res = result.value;
   let tagged = (res.data || []).map((r) => tagRow(type, r));
   if (config.supportsState === "client" && filters.state_id) {
    tagged = tagged.filter((row) => String(row.raw.state_id) === String(filters.state_id));
   }
   const total = res.total_count ?? tagged.length;
   if (total > tagged.length) {
    newNotices.push({ kind: "info", text: `Showing ${tagged.length} of ${total} deleted ${config.entityName} — switch to the "${config.label}" filter and use pagination to see the rest.` });
   }
   merged.push(...tagged);
  });

  merged.sort((a, b) => new Date(b.deletedAt || 0) - new Date(a.deletedAt || 0));

  setRows(merged);
  setTotalCount(merged.length);
  setTotalPages(1);
  setCurrentPage(1);
  setNotices(newNotices);
 }, [debouncedSearch, filters.state_id]);

 const loadData = useCallback(async (page = 1) => {
  setLoading(true);
  try {
   if (entityType) {
    await loadSingle(entityType, page);
   } else {
    await loadAll();
   }
  } catch (err) {
   console.error("Failed to load deleted records", err);
   setToast({ type: "error", message: "Failed to load deleted records." });
  } finally {
   setLoading(false);
  }
 }, [entityType, loadSingle, loadAll]);

 useEffect(() => {
  if (!canLoad) return;
  setCurrentPage(1);
  loadData(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [canLoad, entityType, debouncedSearch, filters.state_id]);

 const handleRestore = async (row) => {
  if (restoringKey) return;
  setRestoringKey(row.key);
  try {
   const result = await ENTITY_CONFIG[row.type].restore(row.raw);
   // A restored user gets back only the assignments that are still valid;
   // name the ones that could not come back (e.g. a school another PO now
   // holds) so the admin can act on them instead of missing them.
   const skipped = result?.data?.skipped_assignments || [];
   if (skipped.length > 0) {
    const parts = skipped.map((s) => `${s.name || `${s.type} #${s.id}`} (${s.reason})`);
    setToast({
     type: "error",
     message: `${ENTITY_CONFIG[row.type].label} restored, but ${skipped.length} assignment(s) could not be restored: ${parts.join("; ")}.`,
    });
   } else {
    setToast({ type: "success", message: `${ENTITY_CONFIG[row.type].label} restored successfully.` });
   }
   // Re-fetch from the server instead of a local splice: a restore can
   // cascade (e.g. restoring a State also restores its Districts/Blocks/
   // Schools server-side), and those other rows are already sitting in
   // `rows` from the initial load — only a re-fetch reflects that.
   await loadData(currentPage);
  } catch (err) {
   console.error("Restore failed", err);
   const detail = err.response?.data?.detail || `Failed to restore ${ENTITY_CONFIG[row.type].label.toLowerCase()}.`;
   setToast({ type: "error", message: detail });
  } finally {
   setRestoringKey(null);
  }
 };

 const activeConfig = entityType ? ENTITY_CONFIG[entityType] : null;
 const searchDisabled = activeConfig ? !activeConfig.supportsSearch : false;
 const stateFilterVisible = entityType ? activeConfig.supportsState !== false : true;

 const filterOptions = useMemo(() => ([
  {
   key: "state_id",
   label: "State",
   type: "select",
   options: states.map((s) => ({ value: String(s.id), label: s.name })),
  },
 ]), [states]);

 if (!roleLoading && !canManageCompetencies) {
  return (
   <div className="p-6">
    <div className="bg-white rounded-lg border border-[#e5e7eb] p-10 text-center max-w-lg mx-auto">
     <div className="w-14 h-14 bg-[#f3f4f6] rounded-lg flex items-center justify-center mx-auto mb-4">
      <HiOutlineLockClosed className="text-2xl text-[#8b949e]" />
     </div>
     <h3 className="m-0 text-base font-semibold text-[#24292f]">Access Restricted</h3>
     <p className="m-0 mt-2 text-sm text-[#57606a]">You don't have permission to view the Restore Center.</p>
    </div>
   </div>
  );
 }

 return (
  <div className="p-6">
   {toast && (
    <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
   )}

   <div className="mb-5">
    <h1 className="text-xl font-bold text-[#24292f]">Restore Center</h1>
    <p className="text-sm text-[#57606a] mt-0.5">Find and restore soft-deleted records across the application</p>
   </div>

   <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-x-auto">
    {/* Toolbar */}
    <div className="flex flex-wrap justify-between items-center gap-3 px-6 py-4 border-b border-[#e5e7eb]">
     <div className="relative flex items-center flex-1 max-w-sm">
      <HiOutlineSearch className="absolute left-3.5 text-[#8b949e] text-sm" />
      <input
       type="text"
       className="w-full py-2.5 pr-8 pl-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border placeholder:text-[#8b949e] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10 disabled:bg-[#f6f8fa] disabled:cursor-not-allowed"
       placeholder={searchDisabled ? "Search not available for this type" : "Search by name…"}
       value={search}
       onChange={(e) => setSearch(e.target.value)}
       disabled={searchDisabled}
      />
      {search && !searchDisabled && (
       <button
        className="absolute right-2 bg-transparent border-none text-[#8b949e] cursor-pointer p-1 rounded-md hover:text-[#57606a] hover:bg-[#f3f4f6] transition-colors"
        onClick={() => setSearch("")}
       >
        <HiOutlineX className="text-sm" />
       </button>
      )}
     </div>

     <div className="flex items-center gap-2">
      <select
       value={entityType}
       onChange={(e) => setEntityType(e.target.value)}
       className="h-10 px-3 pr-8 text-sm border border-[#d0d5dd] rounded-md bg-white text-[#24292f] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
      >
       {ENTITY_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
       ))}
      </select>

      {stateFilterVisible && (
       <FilterDropdown
        filters={filterOptions}
        values={filters}
        onChange={(newFilters) => setFilters(newFilters)}
       />
      )}
     </div>
    </div>

    {/* Notices (All-type overflow / partial failures) */}
    {notices.length > 0 && (
     <div className="px-6 py-3 border-b border-[#e5e7eb] bg-amber-50 space-y-1">
      {notices.map((n, i) => (
       <p key={i} className={`m-0 text-xs flex items-center gap-1.5 ${n.kind === "error" ? "text-red-700" : "text-amber-700"}`}>
        <HiOutlineExclamationCircle className="text-sm flex-shrink-0" />
        {n.text}
       </p>
      ))}
     </div>
    )}

    {/* Table */}
    {loading ? (
     <div className="p-8 space-y-4">
      {[1, 2, 3, 4].map((i) => (
       <div key={i} className="flex items-center gap-4 animate-pulse">
        <div className="w-16 h-4 bg-[#e5e7eb] rounded" />
        <div className="flex-1 h-4 bg-[#e5e7eb] rounded" />
        <div className="w-32 h-4 bg-[#e5e7eb] rounded" />
        <div className="w-32 h-4 bg-[#e5e7eb] rounded" />
        <div className="w-24 h-4 bg-[#e5e7eb] rounded" />
        <div className="w-20 h-4 bg-[#e5e7eb] rounded" />
       </div>
      ))}
     </div>
    ) : rows.length === 0 ? (
     <div className="text-center py-16 px-5">
      <div className="w-16 h-16 bg-[#f3f4f6] rounded-lg flex items-center justify-center mx-auto mb-4">
       <HiOutlineTrash className="text-2xl text-[#8b949e]" />
      </div>
      <p className="m-0 text-sm font-medium text-[#57606a]">No deleted records found.</p>
      <p className="m-0 text-xs text-[#8b949e] mt-1">Nothing matches the current filters.</p>
     </div>
    ) : (
     <table className="w-full text-sm">
      <thead>
       <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
        <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Type</th>
        <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Name / Identifier</th>
        <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Context</th>
        <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Deleted At</th>
        <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Deleted By</th>
        <th className="text-right py-4 px-6 text-xs font-medium text-[#57606a]">Action</th>
       </tr>
      </thead>
      <tbody className="divide-y divide-[#e5e7eb]">
       {rows.map((row) => (
        <tr key={row.key} className="hover:bg-gray-50 transition-colors duration-150">
         <td className="py-4 px-6">
          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${ENTITY_CONFIG[row.type].badge}`}>
           {ENTITY_CONFIG[row.type].label}
          </span>
         </td>
         <td className="py-4 px-6 font-medium text-[#24292f]">{row.name}</td>
         <td className="py-4 px-6">
          <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-[#f3f4f6] text-[#57606a] rounded">
           {row.context}
          </span>
         </td>
         <td className="py-4 px-6 text-[#57606a] text-xs whitespace-nowrap">{formatDate(row.deletedAt)}</td>
         <td className="py-4 px-6 text-[#57606a] text-xs max-w-[160px] truncate" title={row.deletedBy || ""}>
          {row.deletedBy ? (userNames[row.deletedBy] || row.deletedBy) : "—"}
         </td>
         <td className="py-4 px-6 text-right">
          <button
           onClick={() => handleRestore(row)}
           disabled={restoringKey === row.key}
           className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe] disabled:opacity-50 disabled:cursor-not-allowed"
          >
           <HiOutlineRefresh className="text-xs" />
           {restoringKey === row.key ? "Restoring…" : "Restore"}
          </button>
         </td>
        </tr>
       ))}
      </tbody>
     </table>
    )}

    {/* Pagination — only meaningful for a single selected entity type */}
    {!loading && entityType && totalCount > 0 && (
     <Pagination
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount}
      pageSize={PAGE_SIZE}
      onPageChange={(p) => loadData(p)}
      entityName={ENTITY_CONFIG[entityType].entityName}
     />
    )}
   </div>
  </div>
 );
}

export default RestoreCenter;
